import { buildParentNavSections, SIDEBAR_FOOTER_ITEMS } from '@/components/layout/parent-nav';
import { buildTrferNavSections, TRFER_FOOTER_ITEMS } from '@/components/layout/trfer-nav';
import { buildAdminNavSections } from '@/components/layout/admin-nav';
import type { NavItemDef, NavSection } from '@/components/layout/sidebar-primitives';
import { NAV_KEY, navLabel } from '@/i18n/keys/nav';
import { homeFor } from '@/lib/home-route';
import type { User } from '@/types/api';

/**
 * The canonical parent of a page, as data (design grammar §4.2).
 *
 * §4.2: "Every depth-2 page has one canonical parent — its sidebar item —
 * regardless of where the visitor came from. `/trfer/families/:id` is
 * reachable from Families (and its Needs you view), Threads, Reimbursements, Spend,
 * Family assignments and the cohort lens; its `BackLink` is always
 * `← Families`. Referrer-aware back links are history in disguise (§4.6)."
 *
 * This module is the sibling of `home-route.ts`: `homeFor(role)` answers
 * "where does this READER start", `parentFor(pathname)` answers "what is this
 * PAGE a detail of". There is no referrer and no history stack here: the
 * in-app origin ("where did THIS visit come from") lives in
 * `lib/navigation-origin.ts`, and `BackLink` prefers it when there is one
 * (wave w1024, 2026-10-24, reversing §4.2's fixed parent). This module is
 * the FALLBACK — the answer for a deep link, a push notification, a fresh
 * tab — and it is role-aware: a canonical parent the reader's role cannot
 * open is replaced by `homeFor(role)` (`ROLE_REACH`, below), so an admin on
 * a family record is never sent to a page that bounces them.
 *
 * THE ONE EXCEPTION IS A SHARED ROUTE (`ROLE_PARENTS`, below). A handful of
 * routes are mounted once but reached by two different shells, and a shell is
 * not a referrer — it is the reader's whole map. `/apply/status` is the
 * clearest case: an applicant has no sidebar at all, a parent has the family
 * sidebar, and no single path is above the page for both. That is why the
 * optional second argument exists, and why it is an explicit declared table
 * rather than a rule: the set is small, closed, and each row has a reason.
 *
 * WHY IT READS THE NAV MODULES rather than carrying its own table. §4.4 gives
 * a destination ONE label, declared once in `parent-nav.ts` / `trfer-nav.ts` /
 * `admin-nav.ts`. A second table here would be the `HelpPage` defect all over
 * again (#611): two hand-maintained lists that cannot be diffed by review, and
 * a back link that names a nav item nobody can find. So the label a `BackLink`
 * shows is the label the sidebar shows, by construction — rename the nav item
 * and every back link follows in the same commit.
 *
 * The navs are built with EVERY permission granted. A page's parent cannot
 * depend on the reader's grants: a TRFer without `can_review_applications`
 * never reaches `/trfer/applications/:id` at all, so gating the lookup would
 * only ever produce a wrong answer, never a safer one.
 */

/** A page's canonical parent — where its `BackLink` goes, and what it says. */
export interface CanonicalParent {
  /** The parent route's path. Always a route `App.tsx` declares. */
  path: string;
  /** The parent's label, read from the nav module that declares it. */
  label: string;
}

/** The second argument of `parentFor` — see `ROLE_PARENTS` and `ROLE_REACH`. */
export interface ParentForOptions {
  /**
   * The reader's role (`user.role`). Consulted for a route two shells share,
   * and to replace a parent this role cannot open with its home. A caller
   * that has no user in hand omits it and gets the role-independent answer.
   */
  role?: string;
}

/**
 * Hub children (§4.2) — depth-1 pages reachable only from tiles on a sidebar
 * destination, whose parent is therefore NOT a prefix of their own path.
 *
 * "It uses the depth-2 wayfinding form — `← Fundraising` and
 * `Fundraising · CRM` — because from the visitor's seat it is a detail of the
 * hub. Adding it to the sidebar as well would violate §4.4." (The fundraising
 * hub and its tiles retired on 2026-09-17; the rule they illustrated stands.)
 *
 * A hub child has no sidebar entry by definition, so — unlike every other
 * parent in this module — its label cannot be read from a nav module. It is
 * declared here, once, for each one that is itself the parent of something
 * deeper; a hub child may become a parent later and a half-filled table
 * invites a wrong answer.
 *
 * `/admin/handoff-config` left this table in the 2026-09-20 admin-section
 * subtraction and came back 2026-09-21 as a genuine `admin-nav.ts` row, so it
 * gets its label from the nav module like every other sidebar destination.
 */
export const HUB_CHILDREN: Record<string, { hub: string; label: string }> = {
  // The companion. It deliberately has NO nav entry (#844: "the right-rail
  // widget and the dashboard card already offer it on the same screen, and
  // three entry points were two too many") — a page offered only from tiles
  // on a sidebar destination, which is the §4.2 definition of a hub child.
  // The label is the one the mobile `BottomNav` slot uses.
  '/chat': { hub: '/dashboard', get label() { return navLabel(NAV_KEY.tabChat); } },
};

/**
 * Routes ONE mount serves two shells, where the parent differs by shell
 * (#1456). Read before every other rule, and only when a role is supplied.
 *
 * A value of `null` means "this reader has no parent for this page" — the
 * same answer, and the same empty render, as a page with no parent at all.
 *
 * A path value is resolved to its label the same way every other parent is:
 * out of the nav module that owns the destination (§4.4). Nothing here spells
 * a label, so the rename rule still holds.
 *
 * `/apply/status` — the shared applicant/parent route (`App.tsx`, one
 * `RoleGatedRoute allowedRoles={['applicant', 'parent']}`):
 *   - `parent`: the family home. A parent who lands here (a bookmark, a link
 *     in an email) is a reader with a whole app behind them; their home is the
 *     one surface that is above this page for them.
 *   - `applicant`: `null` — see the DECISIONS entry of 2026-08-29. The
 *     applicant shell renders NO sidebar (`MainLayout.tsx`: `hideSidebar`),
 *     so it has no destination to be a detail of. The two candidates both
 *     land the reader somewhere worse than nowhere: `/apply` while signed in
 *     is the "starting a new application here creates a separate account"
 *     confirm (`ApplyLandingPage.tsx`, the `isAuthenticated` branch), and
 *     `/apply/wizard` bounces a submitted application straight back here
 *     (`ApplicationWizard.tsx`, the read-only redirect). §4.6's rule that a
 *     back link which guesses is worse than no back link decides it.
 *
 * `/settings/legal` — a hub child of TWO Settings surfaces. Longest-ancestor
 * gives `/settings`, which is right for every role but the TRFer, who has
 * their own `/trfer/settings`. This row is what lets `LegalSettingsPage` drop
 * its hand-rolled `backHrefFor` and render a plain `BackLink`.
 */
export const ROLE_PARENTS: Record<string, Partial<Record<string, string | null>>> = {
  '/apply/status':  { parent: '/dashboard', applicant: null },
  '/settings/legal': { trfer: '/trfer/settings' },
};

/**
 * THE UNKNOWN-PATH FALLBACK is not a separate rule, and deliberately so.
 *
 * `/trfer` and `/admin` are themselves sidebar destinations that carry
 * `end: true` precisely because each is a prefix of every page in its shell.
 * So an unrecognised path under either — a route added without a nav entry, a
 * typo'd deep link — already has the shell home as its longest known ancestor
 * and resolves there with the shell home's own nav label. No guessing, no
 * second table, and it stays right when a shell gains a destination.
 *
 * The parent and applicant shells have no such prefix, so an unrecognised path
 * outside `/trfer` and `/admin` gets `null` rather than a guess: the applicant
 * shell's pages own their wayfinding (§4.6 — "wizards are not pages, a
 * wizard's Back is its own PillButton"), and a back link that guesses is worse
 * than no back link, being history-based back wearing a label.
 */

function itemsOf(sections: NavSection[]): NavItemDef[] {
  return sections.flatMap((s) => s.items);
}

/**
 * Every sidebar destination, across all three shells, as href → label.
 *
 * Built once: the nav data is static module data, and §4.2's canonical parent
 * is static with it.
 */
const NAV_ITEMS: ReadonlyMap<string, NavItemDef> = (() => {
  const out = new Map<string, NavItemDef>();
  const add = (items: NavItemDef[]) => {
    for (const item of items) if (!out.has(item.href)) out.set(item.href, item);
  };
  add(itemsOf(buildParentNavSections({ showHealth: true })));
  add(SIDEBAR_FOOTER_ITEMS);
  add(itemsOf(buildTrferNavSections({ canAssignTrfers: true, canReviewApplications: true })));
  add(TRFER_FOOTER_ITEMS);
  add(itemsOf(buildAdminNavSections({ canReviewApplications: true })));
  return out;
})();

/**
 * The hrefs are static; the LABELS are not. Each nav item's `name` is a
 * getter over its nav key (#2109), so this map keeps the item and reads
 * `name` on every `get` — a map of strings built at import time froze the
 * back link's label in English (`← Families` under Spanish, 2026-09-23).
 */
export const NAV_DESTINATIONS: ReadonlyMap<string, string> = {
  get size() { return NAV_ITEMS.size; },
  get: (href: string) => NAV_ITEMS.get(href)?.name,
  has: (href: string) => NAV_ITEMS.has(href),
  keys: () => NAV_ITEMS.keys(),
  *values() { for (const item of NAV_ITEMS.values()) yield item.name; },
  *entries() { for (const [href, item] of NAV_ITEMS) yield [href, item.name] as [string, string]; },
  forEach(cb: (value: string, key: string, map: ReadonlyMap<string, string>) => void) {
    for (const [href, item] of NAV_ITEMS) cb(item.name, href, NAV_DESTINATIONS);
  },
  [Symbol.iterator]() { return this.entries(); },
} as ReadonlyMap<string, string>;

/** Strip the query and hash, drop a trailing slash. `/trfer/` is `/trfer`. */
export function normalisePath(pathname: string): string {
  const bare = pathname.split('?')[0].split('#')[0];
  if (!bare.startsWith('/')) return '/';
  return bare.length > 1 ? bare.replace(/\/+$/, '') || '/' : bare;
}

/** `/trfer/families` is an ancestor of `/trfer/families/abc`, not of `/trfer/families-old`. */
function isAncestor(candidate: string, path: string): boolean {
  return path.startsWith(candidate === '/' ? '/' : `${candidate}/`);
}

function labelOf(path: string): string | null {
  return NAV_DESTINATIONS.get(path) ?? HUB_CHILDREN[path]?.label ?? null;
}

/**
 * The canonical parent of `pathname`, or `null` when the page has none.
 *
 * `null` means one of three things, and all three want the same treatment
 * (render no back link):
 *   - the page IS a sidebar destination — depth 0, nothing above it;
 *   - the page owns its own wayfinding (the applicant wizard, renewal,
 *     re-consent, account closure) — §4.6;
 *   - the page is outside every shell (login, the legal library, the public
 *     application landing), where there is no nav to name a parent with.
 *
 * `options.role` is the READER's role (the active hat), consulted twice: for
 * a route two shells share, where no single path is above the page for both
 * (`ROLE_PARENTS`), and AFTER resolution, when the parent is a page the hat
 * cannot open (`ROLE_REACH`) — then the answer is `homeFor(role)` (step 4).
 * Omit it and every path still resolves to the role-independent answer.
 *
 * Resolution order:
 *   0. a shared route with a row for this role → that row (path or `null`);
 *   1. a sidebar destination → `null`;
 *   2. a hub child → its hub (§4.2), because the hub is not its prefix;
 *   3. the LONGEST ancestor that is a sidebar destination or a hub child —
 *      this is what makes `/trfer/families/:id` say `← Families` no matter
 *      which of its seven entry points the visitor used, and what gives a
 *      page mounted under two prefixes "the parent of the prefix it is
 *      mounted under" (`/admin/copy` → Admin dashboard, `/trfer/copy` → a
 *      depth-0 destination with no parent at all). Inside `/trfer/*` and
 *      `/admin/*` this doubles as the unknown-path fallback — see the note
 *      above the nav table;
 *   4. with a known role, a parent from 1–3 that the role cannot open →
 *      the reader's home (`homeParent`), or `null` on the home itself.
 */
export function parentFor(pathname: string, options: ParentForOptions = {}): CanonicalParent | null {
  const parent = canonicalParent(pathname, options);
  const role = options.role;
  // No role, or a role this table does not know: the role-independent answer
  // (a caller with no user in hand, and every pre-existing test, see the page
  // the way they always did).
  if (!parent || role === undefined || !(role in ROLE_REACH)) return parent;
  if (roleCanOpen(parent.path, role)) return parent;
  // The canonical parent is a page this hat cannot open — `RoleGatedRoute`
  // would bounce the reader to `homeFor(role)` anyway, so go there directly
  // and say so: an admin on `/trfer/threads/:id` reads "← Admin dashboard",
  // not "← Threads" followed by a redirect.
  return homeParent(pathname, role);
}

/**
 * The reader's home as a back target, or `null` when the page IS the home.
 * The label is the home's own nav label; the native TRFer home (`/trfer/m`)
 * has no sidebar row, so it borrows the desktop Today's.
 */
export function homeParent(pathname: string, role: string | undefined): CanonicalParent | null {
  const home = homeFor(role as User['role'] | undefined);
  if (normalisePath(pathname) === home) return null;
  const label = homeLabel(home);
  return label ? { path: home, label } : null;
}

/**
 * The nav label of a home path (`homeFor(role)`), or `null` for a home with
 * none (the applicant wizard). Recovery pages read it for "Back to {home}".
 */
export function homeLabel(home: string): string | null {
  return labelOf(home) ?? (home === '/trfer/m' ? labelOf('/trfer') : null);
}

/** `parentFor` before the role check: the page's own canonical parent. */
function canonicalParent(pathname: string, options: ParentForOptions): CanonicalParent | null {
  const path = normalisePath(pathname);

  if (options.role !== undefined) {
    const shared = ROLE_PARENTS[path]?.[options.role];
    // `null` is an answer ("no parent for this reader"); `undefined` is "no
    // row", which falls through to the role-independent rules below.
    if (shared === null) return null;
    if (shared !== undefined) {
      const label = labelOf(shared);
      return label ? { path: shared, label } : null;
    }
  }

  if (NAV_DESTINATIONS.has(path)) return null;

  const hubChild = HUB_CHILDREN[path];
  if (hubChild) {
    const label = labelOf(hubChild.hub);
    return label ? { path: hubChild.hub, label } : null;
  }

  let best: string | null = null;
  for (const candidate of [...NAV_DESTINATIONS.keys(), ...Object.keys(HUB_CHILDREN)]) {
    if (!isAncestor(candidate, path)) continue;
    if (best === null || candidate.length > best.length) best = candidate;
  }
  if (best) {
    const label = labelOf(best);
    if (label) return { path: best, label };
  }

  return null;
}

/**
 * WHO MAY OPEN WHAT — the reach of each active role (`user.role`, the hat).
 *
 * Mirrors the `RoleGatedRoute` groups in `App.tsx` as path prefixes, without
 * importing `App.tsx` (this module ships in the bundle; the router tree is
 * test-time introspection). `parentFor.test.ts` walks `App.tsx`'s route table
 * (`lib/route-table.ts`, `routeEntries`) and fails on any route whose gate
 * disagrees with this table, so the two cannot drift silently.
 *
 * Read by two callers:
 *   - `parentFor(path, { role })` — a canonical parent the hat cannot open
 *     becomes `homeFor(role)`;
 *   - `BackLink` — an in-app origin the hat cannot open (the reader switched
 *     hats since) is ignored and the fallback is used.
 *
 * Reachability only, never permissions: a TRFer without `can_assign_trfers`
 * never reaches a Users child at all (the module comment above).
 *
 * `/trfer/families` for the admin hat is AHEAD of `App.tsx` at BASE on
 * purpose: wave w1024 decision 3 ("the admin hat opens Families") lands the
 * gate change in lane L2. Until it does, the only effect is that an admin's
 * fallback from a family record names Families, which is what BASE did.
 */
const NEUTRAL_PATHS = ['/settings', '/help', '/reconsent', '/account/close'] as const;
const FAMILY_PATHS = [
  '/dashboard', '/chat', '/conversations', '/tasks', '/bills', '/grants',
  '/circle', '/calendar', '/medical', '/community',
] as const;
const APPLY_PATHS = ['/apply/wizard', '/apply/status', '/renew'] as const;
/** `/trfer/*` routes the admin hat opens too (the `['admin', 'trfer']` groups). */
const STAFF_SHARED_PATHS = [
  '/trfer/users', '/trfer/signups', '/trfer/grants', '/trfer/applications',
  '/trfer/committee-package', '/trfer/pipeline', '/trfer/offers',
  '/trfer/copy', '/trfer/forms', '/trfer/families', '/trfer/hospitals',
] as const;

export const ROLE_REACH: Readonly<Record<string, readonly string[]>> = {
  // `/dashboard` is the parent home; `DashboardRoute` redirects every other hat.
  parent: [...FAMILY_PATHS, ...APPLY_PATHS, ...NEUTRAL_PATHS],
  applicant: [...APPLY_PATHS, ...NEUTRAL_PATHS],
  trfer: ['/trfer', ...NEUTRAL_PATHS],
  admin: ['/admin', ...STAFF_SHARED_PATHS, ...FAMILY_PATHS.filter((p) => p !== '/dashboard'), ...NEUTRAL_PATHS],
};

function under(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(`${prefix}/`);
}

/**
 * Can the active role `role` open `pathname` without a redirect? A query or
 * hash is ignored. An unknown role opens nothing.
 */
export function roleCanOpen(pathname: string, role: string): boolean {
  const path = normalisePath(pathname);
  return (ROLE_REACH[role] ?? []).some((prefix) => under(path, prefix));
}

/**
 * Which shell a path belongs to — the unit an in-app origin may not cross.
 *
 * `staff` is `/trfer/*` and `/admin/*`; `family` is the parent and applicant
 * shell; `neutral` is a page every signed-in role shares (Settings, Help,
 * re-consent, account closure), which joins either side; `outside` is
 * everything else — sign-in, the public landing, the legal library, an
 * unknown address — and is never an origin.
 */
export type Shell = 'staff' | 'family' | 'neutral' | 'outside';

export function shellOf(pathname: string): Shell {
  const path = normalisePath(pathname);
  if (under(path, '/trfer') || under(path, '/admin')) return 'staff';
  if (NEUTRAL_PATHS.some((p) => under(path, p))) return 'neutral';
  if ([...FAMILY_PATHS, ...APPLY_PATHS].some((p) => under(path, p))) return 'family';
  return 'outside';
}
